import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API
});

// Add authorization header from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Export as default
export default api;

// Scooter API calls
export const scooterApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API}/scooters`, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API}/scooters/${id}`);
    return response.data;
  },

  getPublic: async (id) => {
    const response = await axios.get(`${API}/scooters/public/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/scooters/stats`);
    return response.data;
  },

  getSalesStats: async () => {
    const response = await axios.get(`${API}/scooters/sales-stats`);
    return response.data;
  },

  getServiceAlerts: async () => {
    const response = await axios.get(`${API}/scooters/service-alerts`);
    return response.data;
  },

  getViewStats: async () => {
    const response = await axios.get(`${API}/scooters/view-stats`);
    return response.data;
  },

  getShowroom: async (params = {}) => {
    const response = await axios.get(`${API}/scooters/showroom`, { params });
    return response.data;
  },

  getFilterOptions: async () => {
    const response = await axios.get(`${API}/scooters/filter-options`);
    return response.data;
  },

  create: async (data) => {
    const response = await axios.post(`${API}/scooters`, data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.put(`${API}/scooters/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API}/scooters/${id}`);
    return response.data;
  },

  uploadPhoto: async (id, file, photoType = "general") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("photo_type", photoType);
    const response = await axios.post(`${API}/scooters/${id}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  uploadDocument: async (id, file, docType = "general") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    const response = await axios.post(`${API}/scooters/${id}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  deletePhoto: async (scooterId, filename) => {
    const response = await axios.delete(`${API}/scooters/${scooterId}/photos/${filename}`);
    return response.data;
  },

  deleteDocument: async (scooterId, filename) => {
    const response = await axios.delete(`${API}/scooters/${scooterId}/documents/${filename}`);
    return response.data;
  },

  reorderPhotos: async (scooterId, photos) => {
    const response = await axios.put(`${API}/scooters/${scooterId}/photos/reorder`, { photos });
    return response.data;
  },

  // Reception & Preparation
  markReadyForSale: async (scooterId, sellingPrice = 0) => {
    const response = await axios.post(`${API}/scooters/${scooterId}/mark-ready-for-sale?selling_price=${sellingPrice}`);
    return response.data;
  },

  addRepairTask: async (scooterId, task) => {
    const response = await axios.post(`${API}/scooters/${scooterId}/repair-tasks`, task);
    return response.data;
  },

  updateRepairTask: async (scooterId, taskId, update) => {
    const response = await axios.put(`${API}/scooters/${scooterId}/repair-tasks/${taskId}`, update);
    return response.data;
  },

  deleteRepairTask: async (scooterId, taskId) => {
    const response = await axios.delete(`${API}/scooters/${scooterId}/repair-tasks/${taskId}`);
    return response.data;
  },

  uploadReceptionPhoto: async (scooterId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API}/scooters/${scooterId}/reception-photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
};

// Parts API calls
export const partsApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API}/parts`, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API}/parts/${id}`);
    return response.data;
  },

  getPublic: async (id) => {
    const response = await axios.get(`${API}/parts/public/${id}`);
    return response.data;
  },

  getShowroom: async (params = {}) => {
    const response = await axios.get(`${API}/parts/showroom`, { params });
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get(`${API}/parts/stats`);
    return response.data;
  },

  getCategories: async () => {
    const response = await axios.get(`${API}/parts/categories`);
    return response.data;
  },

  create: async (data) => {
    const response = await axios.post(`${API}/parts`, data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.put(`${API}/parts/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API}/parts/${id}`);
    return response.data;
  },

  uploadPhoto: async (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API}/parts/${id}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  deletePhoto: async (partId, filename) => {
    const response = await axios.delete(`${API}/parts/${partId}/photos/${filename}`);
    return response.data;
  },
};

export const PART_CATEGORIES = {
  moteur: { label: "Moteur", icon: "⚙️" },
  transmission: { label: "Transmission", icon: "🔄" },
  freins: { label: "Freins", icon: "🛑" },
  electrique: { label: "Électrique", icon: "⚡" },
  carrosserie: { label: "Carrosserie", icon: "🛡️" },
  suspension: { label: "Suspension", icon: "🔧" },
  echappement: { label: "Échappement", icon: "💨" },
  roues: { label: "Roues", icon: "🛞" },
  accessoires: { label: "Accessoires", icon: "🎒" },
  autres: { label: "Autres", icon: "📦" },
};

export const PART_CONDITIONS = {
  neuf: { label: "Neuf", color: "bg-green-500" },
  bon: { label: "Bon état", color: "bg-blue-500" },
  use: { label: "Usé", color: "bg-yellow-500" },
  reparer: { label: "À réparer", color: "bg-red-500" },
};

// Scooter types for filtering
export const SCOOTER_TYPES = {
  fara_permis: { label: "Fără permis (25 km/h)", icon: "🛵", description: "Sans permis" },
  standard: { label: "Standard (50–125 cc)", icon: "🏍️", description: "Permis AM/A1" },
  maxi: { label: "Maxi-scooter (250–750 cc)", icon: "🔥", description: "Permis A2/A" },
  electric: { label: "Electric", icon: "⚡", description: "Écologique" },
  benzina: { label: "Benzină", icon: "⛽", description: "Essence" },
};

// Damage history for filtering
export const DAMAGE_HISTORY = {
  fara_accidente: { label: "Fără accidente", icon: "✅", color: "bg-green-500" },
  lovit_usor: { label: "Lovit ușor (estetic)", icon: "⚠️", color: "bg-yellow-500" },
  lovit_serios: { label: "Lovit serios (structural)", icon: "🔧", color: "bg-orange-500" },
  reconditionat: { label: "Recondiționat complet", icon: "🔄", color: "bg-blue-500" },
};

export const formatPrice = (price) => {
  return new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(price);
};

export const getFileUrl = (path) => {
  if (!path) return null;
  
  // If it's already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Normalize path
  let normalizedPath = path;
  
  // If path already starts with /api/uploads, use it directly
  if (path.startsWith('/api/uploads/')) {
    normalizedPath = path;
  }
  // If path starts with /uploads/, add /api prefix
  else if (path.startsWith('/uploads/')) {
    normalizedPath = '/api' + path;
  }
  // If path starts with uploads/ (no leading slash), add /api/
  else if (path.startsWith('uploads/')) {
    normalizedPath = '/api/' + path;
  }
  // If path is just a filename or relative path, assume it's in uploads
  else if (!path.startsWith('/')) {
    normalizedPath = '/api/uploads/' + path;
  }
  
  return `${process.env.REACT_APP_BACKEND_URL || ''}${normalizedPath}`;
};

// Get optimized image URL with resizing
export const getOptimizedImageUrl = (path, options = {}) => {
  if (!path) return null;
  
  const { width = 800, quality = 70, thumbnail = false } = options;
  
  // Extract folder and filename from path
  // Path could be: /api/uploads/photos/image.jpg or photos/image.jpg
  let cleanPath = path
    .replace('/api/uploads/', '')
    .replace('/uploads/', '')
    .replace('uploads/', '');
  
  const parts = cleanPath.split('/');
  if (parts.length < 2) return getFileUrl(path);
  
  const folder = parts[0];
  const filename = parts.slice(1).join('/');
  
  let url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/img/${folder}/${filename}`;
  
  const params = new URLSearchParams();
  if (thumbnail) {
    params.append('thumb', 'true');
  } else {
    params.append('w', width.toString());
    params.append('q', quality.toString());
  }
  
  return `${url}?${params.toString()}`;
};

export const getStatusColor = (status) => {
  const colors = {
    in_stock: "bg-status-instock text-black",
    reserved: "bg-status-reserved text-black",
    in_service: "bg-status-inservice text-black",
    sold: "bg-status-sold text-white",
  };
  return colors[status] || "bg-muted text-foreground";
};

export const getStatusLabel = (status) => {
  const labels = {
    reception: "Reception",
    preparation: "Preparation",
    in_stock: "In Stock / In stoc",
    reserved: "Reserved / Rezervat",
    in_service: "In Service / In service",
    sold: "Sold / Vandut",
  };
  return labels[status] || status;
};

export const getStatusDotColor = (status) => {
  const colors = {
    reception: "#A855F7",  // Purple
    preparation: "#F97316", // Orange
    in_stock: "#BEF264",
    reserved: "#FACC15",
    in_service: "#38BDF8",
    sold: "#EF4444",
  };
  return colors[status] || "#A1A1AA";
};
